import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../../api/auth.api';
import { useAuthStore } from '../../stores/auth-store';
import { NotificationBanner } from '../../components/NotificationBanner';
import { Lock, Loader2, Eye, EyeOff, AlertTriangle } from 'lucide-react';

export const ChangePasswordRequiredPage: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const { mutate: forceChangePassword, isLoading, error } = useMutation({
    mutationFn: (newPassword: string) =>
      authApi.forceChangePassword(newPassword),
    onSuccess: () => {
      // Mettre à jour le store pour indiquer que le changement est fait
      const state = useAuthStore.getState();
      if (state.currentUser && state.accessToken && state.refreshToken) {
        setAuth({
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          user: state.currentUser,
          profile: state.profile || undefined,
          society: state.society || undefined,
          requirePasswordChange: false,
        });
      }
      navigate('/dashboard');
    },
  });

  const passwordValidation = useMemo(() => {
    return {
      minLength: newPassword.length >= 8,
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
    };
  }, [newPassword]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!isPasswordValid) {
      setLocalError('Le nouveau mot de passe ne respecte pas tous les critères');
      return;
    }

    if (!passwordsMatch) {
      setLocalError('Les mots de passe ne correspondent pas');
      return;
    }

    forceChangePassword(newPassword);
  };

  const displayError = error || localError;

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-affinicar-secondary tw-to-affinicar-dark tw-p-4">
      <div className="tw-w-full tw-max-w-md">
        {/* Card */}
        <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-overflow-hidden">
          {/* Header */}
          <div className="tw-bg-gradient-to-r tw-from-affinicar-primary tw-to-affinicar-secondary tw-p-8 tw-text-center">
            <div className="tw-mx-auto tw-mb-4">
              <img
                src="/assets/images/affinicar/logo_full_white.png"
                alt="Affinicar"
                className="tw-h-16 tw-w-auto tw-mx-auto"
              />
            </div>
            <h1 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-2">
              Changement de mot de passe requis
            </h1>
            <p className="tw-text-white tw-text-opacity-90">
              Pour des raisons de sécurité, vous devez changer votre mot de passe temporaire
            </p>
          </div>

          {/* Content */}
          <div className="tw-p-8">
            {/* Warning Banner */}
            <div className="tw-mb-6 tw-bg-orange-50 tw-border-l-4 tw-border-orange-400 tw-p-4 tw-rounded">
              <div className="tw-flex tw-items-start">
                <AlertTriangle className="tw-w-5 tw-h-5 tw-text-orange-400 tw-mr-3 tw-flex-shrink-0 tw-mt-0.5" />
                <div>
                  <p className="tw-text-sm tw-text-orange-800 tw-font-medium">
                    Action obligatoire
                  </p>
                  <p className="tw-text-sm tw-text-orange-700 tw-mt-1">
                    Vous devez changer votre mot de passe temporaire avant d'accéder à l'application.
                  </p>
                </div>
              </div>
            </div>

            {displayError && (
              <div className="tw-mb-6">
                <NotificationBanner
                  message={
                    (error as any)?.response?.data?.message ||
                    localError ||
                    'Une erreur est survenue'
                  }
                  type="error"
                />
              </div>
            )}

            <form onSubmit={handleSubmit} className="tw-space-y-6">
              {/* Nouveau mot de passe */}
              <div>
                <label
                  htmlFor="newPassword"
                  className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2"
                >
                  Nouveau mot de passe
                </label>
                <div className="tw-relative">
                  <Lock className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
                  <input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setLocalError(null);
                    }}
                    className="tw-w-full tw-pl-10 tw-pr-12 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-transparent tw-transition-all"
                    placeholder="Nouveau mot de passe"
                    required
                    disabled={isLoading}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="tw-w-5 tw-h-5" /> : <Eye className="tw-w-5 tw-h-5" />}
                  </button>
                </div>

                {/* Validation du mot de passe */}
                {newPassword && (
                  <div className="tw-mt-3 tw-space-y-2">
                    <p className="tw-text-xs tw-text-gray-600 tw-font-medium">
                      Le mot de passe doit contenir :
                    </p>
                    <ul className="tw-space-y-1">
                      <li
                        className={`tw-text-xs tw-flex tw-items-center ${
                          passwordValidation.minLength ? 'tw-text-green-600' : 'tw-text-gray-500'
                        }`}
                      >
                        <span className="tw-mr-2">{passwordValidation.minLength ? '✓' : '○'}</span>
                        Au moins 8 caractères
                      </li>
                      <li
                        className={`tw-text-xs tw-flex tw-items-center ${
                          passwordValidation.hasUppercase ? 'tw-text-green-600' : 'tw-text-gray-500'
                        }`}
                      >
                        <span className="tw-mr-2">{passwordValidation.hasUppercase ? '✓' : '○'}</span>
                        Une majuscule
                      </li>
                      <li
                        className={`tw-text-xs tw-flex tw-items-center ${
                          passwordValidation.hasLowercase ? 'tw-text-green-600' : 'tw-text-gray-500'
                        }`}
                      >
                        <span className="tw-mr-2">{passwordValidation.hasLowercase ? '✓' : '○'}</span>
                        Une minuscule
                      </li>
                      <li
                        className={`tw-text-xs tw-flex tw-items-center ${
                          passwordValidation.hasNumber ? 'tw-text-green-600' : 'tw-text-gray-500'
                        }`}
                      >
                        <span className="tw-mr-2">{passwordValidation.hasNumber ? '✓' : '○'}</span>
                        Un chiffre
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Confirmation */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2"
                >
                  Confirmer le mot de passe
                </label>
                <div className="tw-relative">
                  <Lock className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setLocalError(null);
                    }}
                    className="tw-w-full tw-pl-10 tw-pr-12 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-transparent tw-transition-all"
                    placeholder="Confirmer le mot de passe"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="tw-w-5 tw-h-5" /> : <Eye className="tw-w-5 tw-h-5" />}
                  </button>
                </div>

                {confirmPassword && (
                  <p
                    className={`tw-text-xs tw-mt-2 ${
                      passwordsMatch ? 'tw-text-green-600' : 'tw-text-red-600'
                    }`}
                  >
                    {passwordsMatch ? '✓ Les mots de passe correspondent' : '✗ Les mots de passe ne correspondent pas'}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || !isPasswordValid || !passwordsMatch}
                className="tw-w-full tw-py-3 tw-px-4 tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-text-white tw-font-semibold tw-rounded-lg tw-transition-colors tw-duration-200 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="tw-animate-spin tw-w-5 tw-h-5 tw-mr-2" />
                    Changement en cours...
                  </>
                ) : (
                  'Changer mon mot de passe'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
